class Header {

    private static readonly SUBMENU_WIDTH = 650;
    private static readonly SUBMENU_GAP = 8;
    private static readonly SUBMENU_VIEWPORT_MARGIN = 16;
    private static readonly SUBMENU_BRIDGE_OVERLAP = 6;
    private static readonly SUBMENU_CLOSE_DELAY = 250;

    private _closeTimer: ReturnType<typeof setTimeout> | null = null;
    private _bridge: HTMLElement | null = null;
    private _header: HTMLElement | null = null;
    private _nav: HTMLElement | null = null;

    constructor() {
        this._initDesktop();
        this._initMobile();
    }

    // ============================================================
    // Desktop — hover sous-menu
    // ============================================================
    private _initDesktop(): void {
        this._header = document.querySelector<HTMLElement>('[data-header]');
        this._nav = document.querySelector<HTMLElement>('[data-header-nav]');
        this._bridge = document.querySelector<HTMLElement>('[data-header-submenu-bridge]');
        const items = document.querySelectorAll<HTMLElement>('[data-header-item]');

        if (!this._header || !this._nav || !this._bridge) return;

        const positionSubmenu = (submenu: HTMLElement): void => {
            const navRect = this._nav!.getBoundingClientRect();
            const headerRect = this._header!.getBoundingClientRect();
            let leftPos =
                navRect.left + navRect.width / 2 - Header.SUBMENU_WIDTH / 2;

            if (leftPos < Header.SUBMENU_VIEWPORT_MARGIN) {
                leftPos = Header.SUBMENU_VIEWPORT_MARGIN;
            }
            if (
                leftPos + Header.SUBMENU_WIDTH >
                window.innerWidth - Header.SUBMENU_VIEWPORT_MARGIN
            ) {
                leftPos =
                    window.innerWidth -
                    Header.SUBMENU_WIDTH -
                    Header.SUBMENU_VIEWPORT_MARGIN;
            }

            const top = headerRect.bottom + Header.SUBMENU_GAP;
            const bridgeHeight =
                Header.SUBMENU_GAP + Header.SUBMENU_BRIDGE_OVERLAP;

            this._bridge!.style.top = `${headerRect.bottom}px`;
            this._bridge!.style.height = `${bridgeHeight}px`;
            submenu.style.top = `${top}px`;
            submenu.style.left = `${leftPos}px`;
        };

        const showBridge = (): void => {
            this._bridge!.classList.add('is-visible');
            this._bridge!.removeAttribute('hidden');
        };

        const hideBridge = (): void => {
            this._bridge!.classList.remove('is-visible');
            this._bridge!.setAttribute('hidden', '');
        };

        const relatedNode = (target: EventTarget | null): Node | null =>
            target instanceof Node ? target : null;

        const isInDropdownZone = (target: EventTarget | null): boolean => {
            const node = relatedNode(target);
            if (!node) return false;
            if (this._bridge!.contains(node)) return true;
            return !!document
                .querySelector('[data-header-submenu].is-open')
                ?.contains(node);
        };

        const cancelClose = (): void => {
            if (this._closeTimer !== null) {
                clearTimeout(this._closeTimer);
                this._closeTimer = null;
            }
        };

        const scheduleClose = (): void => {
            cancelClose();
            this._closeTimer = setTimeout(() => {
                this._closeAllSubmenus();
                hideBridge();
                this._closeTimer = null;
            }, Header.SUBMENU_CLOSE_DELAY);
        };

        const repositionOpenSubmenus = (): void => {
            const openSubmenu = document.querySelector<HTMLElement>(
                '[data-header-submenu].is-open',
            );
            if (!openSubmenu) return;
            positionSubmenu(openSubmenu);
        };

        window.addEventListener('resize', repositionOpenSubmenus);
        window.addEventListener('scroll', repositionOpenSubmenus, { passive: true });

        this._bridge.addEventListener('mouseenter', cancelClose);
        this._bridge.addEventListener('mouseleave', (e: MouseEvent) => {
            if (isInDropdownZone(e.relatedTarget)) return;
            scheduleClose();
        });

        items.forEach(item => {
            const trigger = item.querySelector<HTMLElement>('[data-header-trigger]');
            const submenu = item.querySelector<HTMLElement>('[data-header-submenu]');

            if (!trigger || !submenu) return;

            const open = () => {
                cancelClose();
                this._closeAllSubmenus();
                positionSubmenu(submenu);
                showBridge();

                submenu.classList.add('is-open');
                trigger.setAttribute('aria-expanded', 'true');
                submenu.setAttribute('aria-hidden', 'false');
            };

            item.addEventListener('mouseenter', open);

            item.addEventListener('mouseleave', (e: MouseEvent) => {
                if (isInDropdownZone(e.relatedTarget)) return;
                scheduleClose();
            });

            submenu.addEventListener('mouseenter', cancelClose);

            submenu.addEventListener('mouseleave', (e: MouseEvent) => {
                if (isInDropdownZone(e.relatedTarget)) return;
                scheduleClose();
            });
        });
    }

    private _closeAllSubmenus(): void {
        document.querySelectorAll<HTMLElement>('[data-header-submenu].is-open').forEach(submenu => {
            submenu.classList.remove('is-open');
            submenu.setAttribute('aria-hidden', 'true');
            const trigger = submenu.closest('[data-header-item]')?.querySelector<HTMLElement>('[data-header-trigger]');
            if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
    }

    // ============================================================
    // Mobile — panneau + drill-down
    // ============================================================
    private _initMobile(): void {
        const btn = document.querySelector<HTMLElement>('[data-header-mobile-btn]');
        const panel = document.querySelector<HTMLElement>('[data-header-mobile-panel]');
        const slider = document.querySelector<HTMLElement>('[data-header-mobile-slider]');
        const backBtn = document.querySelector<HTMLElement>('[data-header-mobile-back]');
        const backLabel = document.querySelector<HTMLElement>('[data-header-mobile-back-label]');
        const sublist = document.querySelector<HTMLElement>('[data-header-mobile-sublist]');
        const triggers = document.querySelectorAll<HTMLElement>('[data-header-mobile-trigger]');

        if (!btn || !panel || !slider) return;

        const openPanel = () => {
            btn.classList.add('is-open');
            panel.classList.add('is-open');
            panel.setAttribute('aria-hidden', 'false');
            btn.setAttribute('aria-expanded', 'true');
            document.body.style.overflow = 'hidden';
        };

        const closePanel = () => {
            btn.classList.remove('is-open');
            panel.classList.remove('is-open');
            panel.setAttribute('aria-hidden', 'true');
            btn.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
            slider.classList.remove('is-level-2');
        };

        btn.addEventListener('click', () => {
            panel.classList.contains('is-open') ? closePanel() : openPanel();
        });

        triggers.forEach(trigger => {
            trigger.addEventListener('click', () => {
                const title = trigger.dataset.title ?? '';
                const subdataEl = trigger.nextElementSibling as HTMLElement | null;

                if (!subdataEl || !sublist || !backLabel) return;

                // Peupler le panneau niveau 2 depuis les données cachées (structure identique au desktop)
                const sourceItems =
                    subdataEl.querySelectorAll<HTMLElement>(':scope > li');
                sublist.innerHTML = '';
                sourceItems.forEach(src => {
                    const li = document.createElement('li');
                    li.className = 'header-mobile__subgroup';
                    li.innerHTML = src.innerHTML;
                    sublist.appendChild(li);
                });

                backLabel.textContent = title;
                slider.classList.add('is-level-2');
            });
        });

        backBtn?.addEventListener('click', () => {
            slider.classList.remove('is-level-2');
        });
    }

}

export const header = new Header();
